import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type ActivityEvent, EventText, useBoardActivity } from '@/entities/history';
import { useViewer } from '@/entities/viewer';
import { formatDateTime } from '@/shared/lib/datetime';
import { Button, ErrorState, LoadingState } from '@/shared/ui';

import styles from './BoardActivity.module.css';

interface Props {
  onOpenCard: (cardId: string) => void;
  boardId: string;
  onClose: () => void;
}

type Filter = ActivityEvent['entity_type'] | null;
const FILTERS: {
  value: Filter;
  label: 'filterAll' | 'filterCards' | 'filterColumns' | 'filterBoard';
}[] = [
  { value: null, label: 'filterAll' },
  { value: 'card', label: 'filterCards' },
  { value: 'column', label: 'filterColumns' },
  { value: 'board', label: 'filterBoard' },
];

/**
 * The board's Activity feed as a sliding panel (like the Timeline): who did what, newest first,
 * older pages on demand. A task event links to its card. Escape closes it.
 */
export function ActivityDrawer({ boardId, onOpenCard, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const viewer = useViewer();
  const zone = viewer.status === 'authenticated' ? viewer.user.timezone : undefined;
  const [filter, setFilter] = useState<Filter>(null);
  const activity = useBoardActivity(boardId, filter);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  const events = activity.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <aside
      className={styles.panel}
      aria-label={t('history.activityTitle')}
      data-testid="board-activity"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <header className={styles.header}>
        <h2 className={styles.title}>{t('history.activityTitle')}</h2>
        <button
          ref={closeRef}
          type="button"
          className={styles.close}
          aria-label={t('history.activityClose')}
          title={t('history.activityClose')}
          onClick={onClose}
        >
          ×
        </button>
      </header>
      <div className={styles.filters} role="group" aria-label={t('history.filter')}>
        {FILTERS.map((item) => (
          <button
            key={item.label}
            type="button"
            className={`${styles.filter} ${filter === item.value ? styles.filterOn : ''}`}
            aria-pressed={filter === item.value}
            onClick={() => {
              setFilter(item.value);
            }}
          >
            {t(`history.${item.label}`)}
          </button>
        ))}
      </div>
      {activity.isPending && <LoadingState />}
      {activity.isError && (
        <ErrorState error={activity.error} onRetry={() => void activity.refetch()} />
      )}
      {activity.data && events.length === 0 && (
        <p className={styles.empty}>{t('history.activityEmpty')}</p>
      )}
      {events.length > 0 && (
        <ol className={styles.list}>
          {events.map((event) => {
            const title =
              event.entity_type === 'card' && typeof event.payload.card_title === 'string'
                ? event.payload.card_title
                : null;
            return (
              <li key={event.id} className={styles.item} data-testid="activity-item">
                <p className={styles.text}>
                  <EventText event={event} />
                </p>
                <div className={styles.meta}>
                  <time dateTime={event.created_at}>
                    {formatDateTime(event.created_at, i18n.language, zone)}
                  </time>
                  {title !== null && event.card_id && (
                    <button
                      type="button"
                      className={styles.card}
                      title={t('history.openCard')}
                      onClick={() => {
                        if (event.card_id) onOpenCard(event.card_id);
                      }}
                    >
                      {title}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {activity.hasNextPage && (
        <Button
          disabled={activity.isFetchingNextPage}
          onClick={() => void activity.fetchNextPage()}
        >
          {activity.isFetchingNextPage ? t('history.loadingMore') : t('history.more')}
        </Button>
      )}
    </aside>
  );
}
